import React, { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSend,
  FiFileText,
  FiGitMerge,
} from "react-icons/fi";
import { useAuth } from "../../../services/useAuth";
import { isNotificationAdmin } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Loading from "../../../components/CustomUI/Loading";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Checkbox from "../../../components/BaseUI/Checkbox";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Panel,
} from "../../../components/BaseUI/Tabs";
import {
  API_URL,
  EVENT_TYPES,
  SEVERITIES,
  EVENT_TYPE_LABELS,
  SEVERITY_LABELS,
  SEVERITY_LOZENGE,
  TRIGGER_EVENTS,
  getTriggerEventLabel,
  getTargetTypeLabel,
  formatChannels,
  toFriendlyTemplate,
  toBackendTemplate,
  getConditionFieldsForEvent,
  isWorkflowOwnedEvent,
  getDefaultOperatorForField,
  getOperatorsForField,
  CONDITION_FIELD_HINTS,
  selectStyle,
  selectInner,
} from "../Notifications/constants";
import VariableInsertField from "./VariableInsertField";

const emptyTemplateForm = () => ({
  name: "",
  title: "",
  body: "",
  eventType: "custom",
  severity: "info",
  channels: ["inapp"],
  isActive: true,
});

const emptyRuleForm = () => ({
  templateId: "",
  triggerEvent: "",
  targetType: "all",
  targetProfiles: [],
  targetUsers: [],
  cooldownMinutes: 5,
  isActive: true,
  conditions: [],
});

function authHeaders(getToken) {
  return { headers: { Authorization: `Bearer ${getToken()}` } };
}

function resolveCompanyId(user) {
  if (!user) return "";
  if (user.companyId) return String(user.companyId);
  if (user.company?.id) return String(user.company.id);
  if (user.assignedType === "company" && user.assignedId) {
    return String(user.assignedId);
  }
  if (user.role === "company" && user.assignedId) {
    return String(user.assignedId);
  }
  return "";
}

// ─── Templates Tab ───────────────────────────────────────────────────────────
const TemplatesTab = ({ getToken, addToast, onRegisterActions }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTemplateForm());
  const [saving, setSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/notifications/templates`,
        authHeaders(getToken),
      );
      if (data.status) setTemplates(data.templates || []);
    } catch {
      addToast("Şablonlar yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [getToken, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = useCallback(() => {
    setForm(emptyTemplateForm());
    setEditing(null);
    setSidebarOpen(true);
  }, []);

  useEffect(() => {
    onRegisterActions?.({ label: "Yeni Şablon", onClick: openCreate });
    return () => onRegisterActions?.(null);
  }, [onRegisterActions, openCreate]);

  const openEdit = (t) => {
    setForm({
      name: t.name,
      title: toFriendlyTemplate(t.title, t.eventType),
      body: toFriendlyTemplate(t.body, t.eventType),
      eventType: t.eventType,
      severity: t.severity,
      channels: t.channels || ["inapp"],
      isActive: t.isActive !== false,
    });
    setEditing(t);
    setSidebarOpen(true);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setEditing(null);
    setForm(emptyTemplateForm());
  };

  const toggleChannel = (ch) => {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(ch)
        ? f.channels.filter((c) => c !== ch)
        : [...f.channels, ch],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.title.trim()) {
      addToast("Ad ve başlık zorunludur", "danger");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        title: toBackendTemplate(form.title, form.eventType),
        body: toBackendTemplate(form.body, form.eventType),
      };
      if (editing) {
        await axios.put(
          `${API_URL}/api/notifications/templates/${editing._id}`,
          payload,
          authHeaders(getToken),
        );
        addToast("Şablon güncellendi", "success");
      } else {
        await axios.post(
          `${API_URL}/api/notifications/templates`,
          payload,
          authHeaders(getToken),
        );
        addToast("Şablon oluşturuldu", "success");
      }
      closeSidebar();
      load();
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `${API_URL}/api/notifications/templates/${id}`,
        authHeaders(getToken),
      );
      addToast("Şablon silindi", "success");
      load();
    } catch {
      addToast("Silme başarısız", "danger");
    } finally {
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  const handleTest = async (id) => {
    try {
      await axios.post(
        `${API_URL}/api/notifications/templates/${id}/test`,
        {},
        authHeaders(getToken),
      );
      addToast("Test bildirimi gönderildi", "success");
    } catch {
      addToast("Test gönderilemedi", "danger");
    }
  };

  const templateColumns = [
    { name: "Şablon Adı", dataType: "text", id: 0 },
    { name: "Bildirim Türü", dataType: "text", id: 1 },
    { name: "Öncelik", dataType: "text", id: 2 },
    { name: "Gönderim Kanalı", id: 3, sortable: false },
    { name: "Durum", dataType: "text", id: 4 },
    { name: "İşlemler", id: 5, sortable: false },
  ];

  return (
    <>
      <CardContainer>
        <Table columns={templateColumns} loading={loading} search>
          {templates.map((t) => (
            <Tr key={t._id} data={t}>
              <Td>
                <strong style={{ fontSize: "0.875rem" }}>{t.name}</strong>
              </Td>
              <Td>
                <span className="small">
                  {EVENT_TYPE_LABELS[t.eventType] || t.eventType}
                </span>
              </Td>
              <Td>
                <Lozenge
                  appearance={SEVERITY_LOZENGE[t.severity] || "default-subtle"}
                  isBold
                >
                  {SEVERITY_LABELS[t.severity] || t.severity}
                </Lozenge>
              </Td>
              <Td>
                <span className="small">{formatChannels(t.channels)}</span>
              </Td>
              <Td>
                {t.isActive ? (
                  <Lozenge appearance="success-subtle" isBold>
                    Aktif
                  </Lozenge>
                ) : (
                  <Lozenge appearance="default-subtle" isBold>
                    Pasif
                  </Lozenge>
                )}
              </Td>
              <Td>
                <div className="d-flex gap-2">
                  <IconButton
                    icon={<FiEdit2 />}
                    label="Düzenle"
                    appearance="subtle"
                    shape="circle"
                    onClick={() => openEdit(t)}
                  />
                  <IconButton
                    icon={<FiSend />}
                    label="Test"
                    appearance="subtle"
                    shape="circle"
                    onClick={() => handleTest(t._id)}
                  />
                  <IconButton
                    icon={<FiTrash2 />}
                    label="Sil"
                    appearance="subtle"
                    shape="circle"
                    onClick={() => setConfirmModal({ isOpen: true, id: t._id })}
                  />
                </div>
              </Td>
            </Tr>
          ))}
        </Table>
      </CardContainer>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, id: null })}
        width="small"
      >
        <ModalHeader>Şablonu Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page mb-0">
            Bu şablonu silmek istediğinizden emin misiniz?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="danger"
              onClick={() => handleDelete(confirmModal.id)}
            >
              Sil
            </Button>
            <Button
              appearance="subtle"
              onClick={() => setConfirmModal({ isOpen: false, id: null })}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <SidebarContainer
        show={sidebarOpen}
        title={editing ? "Şablonu Düzenle" : "Yeni Şablon"}
        onClose={closeSidebar}
      >
        <div className="d-flex flex-column gap-3" style={{ height: "100%" }}>
          <div>
            <Label required>Şablon Adı</Label>
            <Input
              value={form.name}
              onChange={(v) => setForm((f) => ({ ...f, name: v }))}
              placeholder="Örn. Onay Bekliyor"
            />
          </div>
          <div>
            <Label required>Bildirim Türü</Label>
            <div style={selectStyle}>
              <select
                style={selectInner}
                value={form.eventType}
                onChange={(e) =>
                  setForm((f) => ({ ...f, eventType: e.target.value }))
                }
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {EVENT_TYPE_LABELS[t] || t}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label required>Öncelik</Label>
            <div style={selectStyle}>
              <select
                style={selectInner}
                value={form.severity}
                onChange={(e) =>
                  setForm((f) => ({ ...f, severity: e.target.value }))
                }
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {SEVERITY_LABELS[s] || s}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <VariableInsertField
            label="Bildirim Başlığı"
            required
            value={form.title}
            onChange={(v) => setForm((f) => ({ ...f, title: v }))}
            eventType={form.eventType}
            placeholder="Örn. Yeni onay bekliyor — veya alttaki butonlarla teklif adı ekleyin"
          />
          <VariableInsertField
            label="Bildirim Metni"
            required
            multiline
            value={form.body}
            onChange={(v) => setForm((f) => ({ ...f, body: v }))}
            eventType={form.eventType}
            placeholder="Örn. [Firma adı] için onay bekleniyor — butonlarla alan ekleyebilirsiniz"
          />
          <div>
            <Label>Gönderim Kanalları</Label>
            <div className="d-flex flex-column gap-2 mt-1">
              <Checkbox
                label="Uygulama içi bildirim"
                isChecked={form.channels.includes("inapp")}
                onChange={() => toggleChannel("inapp")}
              />
              <Checkbox
                label="E-posta bildirimi"
                isChecked={form.channels.includes("email")}
                onChange={() => toggleChannel("email")}
              />
              <Checkbox
                label="Anlık bildirim"
                isChecked={form.channels.includes("push")}
                onChange={() => toggleChannel("push")}
              />
            </div>
          </div>
          <div className="mt-auto pt-3">
            <Button
              appearance="primary-animated"
              className="w-100"
              onClick={handleSave}
              disabled={saving}
              icon={editing ? <FiEdit2 /> : <FiPlus />}
            >
              {saving ? "Kaydediliyor..." : editing ? "Güncelle" : "Oluştur"}
            </Button>
          </div>
        </div>
      </SidebarContainer>
    </>
  );
};

// ─── Rules Tab ───────────────────────────────────────────────────────────────
const pickerListStyle = {
  maxHeight: 180,
  overflowY: "auto",
  border: "1px solid #e2e5e8",
  borderRadius: 6,
  padding: "6px 8px",
};

const RulesTab = ({ user, getToken, addToast, onRegisterActions }) => {
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRuleForm());
  const [userSearch, setUserSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const companyId = resolveCompanyId(user);
      const profileParams = {};
      const userParams = {};

      if (companyId) {
        userParams.companyId = companyId;
        if (user?.role === "admin") {
          profileParams.tenantId = companyId;
        }
      }

      const [rRes, tRes, pRes, uRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications/rules`, authHeaders(getToken)),
        axios.get(
          `${API_URL}/api/notifications/templates`,
          authHeaders(getToken),
        ),
        axios.get(`${API_URL}/api/user-profiles`, {
          ...authHeaders(getToken),
          params: profileParams,
        }),
        axios.get(`${API_URL}/api/users`, {
          ...authHeaders(getToken),
          params: userParams,
        }),
      ]);
      if (rRes.data.status) setRules(rRes.data.rules || []);
      if (tRes.data.status) setTemplates(tRes.data.templates || []);
      setAllProfiles(pRes.data?.profiles || []);
      const activeUsers = (uRes.data?.users || []).filter(
        (u) => u.status === "active",
      );
      setAllUsers(activeUsers);
    } catch {
      addToast("Kurallar yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [user, getToken, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = useCallback(() => {
    setForm(emptyRuleForm());
    setEditing(null);
    setUserSearch("");
    setSidebarOpen(true);
  }, []);

  useEffect(() => {
    onRegisterActions?.({ label: "Yeni Kural", onClick: openCreate });
    return () => onRegisterActions?.(null);
  }, [onRegisterActions, openCreate]);

  const openEdit = (r) => {
    setForm({
      templateId: r.templateId?._id || r.templateId || "",
      triggerEvent: r.triggerEvent || "",
      targetType: r.targetType || "all",
      targetProfiles: (r.targetProfiles || []).map((p) => String(p._id || p)),
      targetUsers: (r.targetUsers || []).map((u) => String(u._id || u)),
      cooldownMinutes: r.cooldownMinutes ?? 5,
      isActive: r.isActive !== false,
      conditions: r.conditions || [],
    });
    setEditing(r);
    setUserSearch("");
    setSidebarOpen(true);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setEditing(null);
    setForm(emptyRuleForm());
    setUserSearch("");
  };

  const handleTriggerChange = (val) => {
    const validFields = getConditionFieldsForEvent(val).map((f) => f.value);
    const workflowOwned = isWorkflowOwnedEvent(val);
    setForm((f) => ({
      ...f,
      triggerEvent: val,
      targetType: workflowOwned ? "event_users" : f.targetType,
      targetProfiles: workflowOwned ? [] : f.targetProfiles,
      targetUsers: workflowOwned ? [] : f.targetUsers,
      conditions: f.conditions.filter(
        (c) => !c.field || validFields.includes(c.field),
      ),
    }));
  };

  const toggleProfile = (id) => {
    setForm((f) => ({
      ...f,
      targetProfiles: f.targetProfiles.includes(id)
        ? f.targetProfiles.filter((p) => p !== id)
        : [...f.targetProfiles, id],
    }));
  };

  const toggleUser = (id) => {
    setForm((f) => ({
      ...f,
      targetUsers: f.targetUsers.includes(id)
        ? f.targetUsers.filter((u) => u !== id)
        : [...f.targetUsers, id],
    }));
  };

  const addCondition = () => {
    setForm((f) => ({
      ...f,
      conditions: [...f.conditions, { field: "", operator: "eq", value: "" }],
    }));
  };

  const updateCondition = (i, key, val) => {
    setForm((f) => {
      const conds = [...f.conditions];
      const updated = { ...conds[i], [key]: val };
      if (key === "field" && val) {
        updated.operator = getDefaultOperatorForField(val, f.triggerEvent);
        const allowed = Object.keys(getOperatorsForField(val));
        if (!allowed.includes(updated.operator)) {
          updated.operator = allowed[0] || "eq";
        }
      }
      conds[i] = updated;
      return { ...f, conditions: conds };
    });
  };

  const removeCondition = (i) => {
    setForm((f) => ({
      ...f,
      conditions: f.conditions.filter((_, idx) => idx !== i),
    }));
  };

  const handleSave = async () => {
    if (!form.templateId || !form.triggerEvent.trim()) {
      addToast("Şablon ve tetikleyici olay zorunludur", "danger");
      return;
    }
    if (form.targetType === "profiles" && form.targetProfiles.length === 0) {
      addToast("En az bir profil seçilmelidir", "danger");
      return;
    }
    if (form.targetType === "users" && form.targetUsers.length === 0) {
      addToast("En az bir kullanıcı seçilmelidir", "danger");
      return;
    }
    for (const c of form.conditions) {
      if (!c.field || String(c.value).trim() === "") {
        addToast("Tüm koşullar için alan ve değer zorunludur", "danger");
        return;
      }
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(
          `${API_URL}/api/notifications/rules/${editing._id}`,
          form,
          authHeaders(getToken),
        );
        addToast("Kural güncellendi", "success");
      } else {
        await axios.post(
          `${API_URL}/api/notifications/rules`,
          form,
          authHeaders(getToken),
        );
        addToast("Kural oluşturuldu", "success");
      }
      closeSidebar();
      load();
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `${API_URL}/api/notifications/rules/${id}`,
        authHeaders(getToken),
      );
      addToast("Kural silindi", "success");
      load();
    } catch {
      addToast("Silme başarısız", "danger");
    } finally {
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.patch(
        `${API_URL}/api/notifications/rules/${id}/toggle`,
        {},
        authHeaders(getToken),
      );
      load();
    } catch {
      addToast("Durum değiştirilemedi", "danger");
    }
  };

  const ruleColumns = [
    { name: "Şablon", dataType: "text", id: 0 },
    { name: "Ne Zaman / Koşullar", dataType: "text", id: 1 },
    { name: "Kime Gönderilsin", dataType: "text", id: 2 },
    { name: "Durum", dataType: "text", id: 3 },
    { name: "İşlemler", id: 4, sortable: false },
  ];

  const filteredUsers = allUsers.filter((u) => {
    const q = userSearch.toLowerCase();
    return (
      !q ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q)
    );
  });

  const conditionFields = getConditionFieldsForEvent(form.triggerEvent);

  return (
    <>
      <CardContainer>
        <Table columns={ruleColumns} loading={loading} search>
          {rules.map((r) => (
            <Tr key={r._id} data={r}>
              <Td>
                <strong style={{ fontSize: "0.875rem" }}>
                  {r.templateId?.name || "—"}
                </strong>
              </Td>
              <Td>
                <span className="small">
                  {getTriggerEventLabel(r.triggerEvent)}
                </span>
                {r.conditions?.length > 0 && (
                  <div>
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.72rem" }}
                    >
                      {r.conditions.length} koşul aktif
                    </span>
                  </div>
                )}
              </Td>
              <Td>
                <span className="small">
                  {getTargetTypeLabel(r.targetType)}
                </span>
                {r.targetType === "profiles" &&
                  r.targetProfiles?.length > 0 && (
                    <div>
                      <span
                        className="text-muted"
                        style={{ fontSize: "0.72rem" }}
                      >
                        {r.targetProfiles.length} profil
                      </span>
                    </div>
                  )}
                {r.targetType === "users" && r.targetUsers?.length > 0 && (
                  <div>
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.72rem" }}
                    >
                      {r.targetUsers.length} kullanıcı
                    </span>
                  </div>
                )}
              </Td>
              <Td>
                {r.isActive ? (
                  <Lozenge appearance="success-subtle" isBold>
                    Aktif
                  </Lozenge>
                ) : (
                  <Lozenge appearance="default-subtle" isBold>
                    Pasif
                  </Lozenge>
                )}
              </Td>
              <Td>
                <div className="d-flex gap-2">
                  <Button
                    appearance="subtle"
                    onClick={() => handleToggle(r._id)}
                  >
                    {r.isActive ? "Kapat" : "Aç"}
                  </Button>
                  <IconButton
                    icon={<FiEdit2 />}
                    label="Düzenle"
                    appearance="subtle"
                    shape="circle"
                    onClick={() => openEdit(r)}
                  />
                  <IconButton
                    icon={<FiTrash2 />}
                    label="Sil"
                    appearance="subtle"
                    shape="circle"
                    onClick={() => setConfirmModal({ isOpen: true, id: r._id })}
                  />
                </div>
              </Td>
            </Tr>
          ))}
        </Table>
      </CardContainer>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, id: null })}
        width="small"
      >
        <ModalHeader>Kuralı Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page mb-0">
            Bu kuralı silmek istediğinizden emin misiniz?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="danger"
              onClick={() => handleDelete(confirmModal.id)}
            >
              Sil
            </Button>
            <Button
              appearance="subtle"
              onClick={() => setConfirmModal({ isOpen: false, id: null })}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <SidebarContainer
        show={sidebarOpen}
        title={editing ? "Kuralı Düzenle" : "Yeni Kural"}
        onClose={closeSidebar}
      >
        <div className="d-flex flex-column gap-3" style={{ height: "100%" }}>
          <div>
            <Label required>Şablon</Label>
            <div style={selectStyle}>
              <select
                style={selectInner}
                value={form.templateId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, templateId: e.target.value }))
                }
              >
                <option value="">— Seçin —</option>
                {templates.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label required>Ne Zaman Gönderilsin</Label>
            <div style={selectStyle}>
              <select
                style={selectInner}
                value={form.triggerEvent}
                onChange={(e) => handleTriggerChange(e.target.value)}
              >
                <option value="">— Olay seçin —</option>
                {TRIGGER_EVENTS.map((ev) => (
                  <option key={ev.value} value={ev.value}>
                    {ev.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label required>Kime Gönderilsin</Label>
            <div style={selectStyle}>
              <select
                style={selectInner}
                value={form.targetType}
                disabled={isWorkflowOwnedEvent(form.triggerEvent)}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    targetType: e.target.value,
                    targetProfiles: [],
                    targetUsers: [],
                  }))
                }
              >
                <option value="all">Tüm Kullanıcılar</option>
                <option value="event_users">Akıştaki İlgili Kullanıcılar</option>
                <option value="approval_admins">Tüm Onay Yetkilileri</option>
                <option value="profiles">Profil Bazlı</option>
                <option value="users">Belirli Kullanıcılar</option>
              </select>
            </div>
            {isWorkflowOwnedEvent(form.triggerEvent) && (
              <p className="text-muted small mt-1 mb-0">
                Onay bildirimlerinde alıcılar onay akışındaki adımdan belirlenir.
                Bu kural yalnızca mesaj metnini değiştirir.
              </p>
            )}
          </div>

          {!isWorkflowOwnedEvent(form.triggerEvent) && form.targetType === "profiles" && (
            <div>
              <Label required>Profil Seçin</Label>
              {allProfiles.length === 0 ? (
                <p className="text-muted small mb-0">Profil bulunamadı.</p>
              ) : (
                <div
                  style={pickerListStyle}
                  className="d-flex flex-column gap-1 mt-1"
                >
                  {allProfiles.map((p) => (
                    <Checkbox
                      key={String(p._id)}
                      label={p.name}
                      isChecked={form.targetProfiles.includes(String(p._id))}
                      onChange={() => toggleProfile(String(p._id))}
                    />
                  ))}
                </div>
              )}
              {form.targetProfiles.length > 0 && (
                <p className="text-muted small mt-1 mb-0">
                  {form.targetProfiles.length} profil seçildi
                </p>
              )}
            </div>
          )}

          {!isWorkflowOwnedEvent(form.triggerEvent) && form.targetType === "users" && (
            <div>
              <Label required>Kullanıcı Seçin</Label>
              <Input
                value={userSearch}
                onChange={setUserSearch}
                placeholder="İsim veya e-posta ile ara..."
              />
              <div
                style={pickerListStyle}
                className="d-flex flex-column gap-1 mt-2"
              >
                {filteredUsers.length === 0 ? (
                  <p className="text-muted small mb-0">Kullanıcı bulunamadı.</p>
                ) : (
                  filteredUsers.map((u) => (
                    <Checkbox
                      key={String(u._id)}
                      label={
                        u.name
                          ? `${u.name}${u.email ? ` (${u.email})` : ""}`
                          : u.email || String(u._id)
                      }
                      isChecked={form.targetUsers.includes(String(u._id))}
                      onChange={() => toggleUser(String(u._id))}
                    />
                  ))
                )}
              </div>
              {form.targetUsers.length > 0 && (
                <p className="text-muted small mt-1 mb-0">
                  {form.targetUsers.length} kullanıcı seçildi
                </p>
              )}
            </div>
          )}

          <div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <Label>Ek Koşullar</Label>
              <Button
                appearance="primary-subtle"
                onClick={addCondition}
                disabled={!form.triggerEvent}
              >
                Koşul Ekle
              </Button>
            </div>
            {!form.triggerEvent && (
              <p className="text-muted small mb-2">
                Koşul eklemek için önce olay seçin.
              </p>
            )}
            {form.triggerEvent && form.conditions.length === 0 && (
              <p className="text-muted small mb-2">
                Koşul eklemeden tüm olaylarda gönderilir.
              </p>
            )}
            {form.triggerEvent && form.conditions.length > 0 && (
              <p className="text-muted small mb-2">
                Tüm koşullar sağlanmalıdır.
              </p>
            )}
            {form.conditions.map((c, i) => (
              <div key={i} className="mb-2">
                <div
                  className="d-flex gap-2 align-items-center"
                  style={{ flexWrap: "nowrap" }}
                >
                  <div
                    style={{
                      ...selectStyle,
                      flex: "1 1 0",
                      minWidth: 0,
                    }}
                  >
                    <select
                      style={selectInner}
                      value={c.field}
                      onChange={(e) =>
                        updateCondition(i, "field", e.target.value)
                      }
                    >
                      <option value="">Alan seçin</option>
                      {conditionFields.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div
                    style={{
                      ...selectStyle,
                      flex: "0 0 118px",
                      width: 118,
                    }}
                  >
                    <select
                      style={selectInner}
                      value={c.operator}
                      onChange={(e) =>
                        updateCondition(i, "operator", e.target.value)
                      }
                    >
                      {Object.entries(getOperatorsForField(c.field)).map(
                        ([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                  <div style={{ flex: "0 0 52px", width: 52 }}>
                    <Input
                      value={c.value}
                      onChange={(v) => updateCondition(i, "value", v)}
                      placeholder="Değer"
                      style={{ width: "100%" }}
                    />
                  </div>
                  <IconButton
                    icon={<FiTrash2 />}
                    label="Sil"
                    appearance="subtle"
                    shape="circle"
                    onClick={() => removeCondition(i)}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-auto pt-3">
            <Button
              appearance="primary-animated"
              className="w-100"
              onClick={handleSave}
              disabled={saving}
              icon={editing ? <FiEdit2 /> : <FiPlus />}
            >
              {saving ? "Kaydediliyor..." : editing ? "Güncelle" : "Oluştur"}
            </Button>
          </div>
        </div>
      </SidebarContainer>
    </>
  );
};

// ─── Analytics Tab ───────────────────────────────────────────────────────────
const AnalyticsTab = ({ getToken, addToast }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/notifications/analytics`, authHeaders(getToken))
      .then((res) => {
        if (res.data.status) setData(res.data.analytics);
      })
      .catch(() => addToast("Analitik yüklenemedi", "danger"))
      .finally(() => setLoading(false));
  }, [getToken, addToast]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Loading />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="row g-3">
      <div className="col-md-4">
        <CardContainer>
          <div className="p-3 text-center">
            <div className="title-page mb-1">{data.totalSent}</div>
            <p className="sub-title-page mb-0">Toplam Gönderilen</p>
          </div>
        </CardContainer>
      </div>
      <div className="col-md-4">
        <CardContainer>
          <div className="p-3 text-center">
            <div className="title-page mb-1">{data.totalRead}</div>
            <p className="sub-title-page mb-0">Okunan</p>
          </div>
        </CardContainer>
      </div>
      <div className="col-md-4">
        <CardContainer>
          <div className="p-3 text-center">
            <div className="title-page mb-1">%{data.readRate}</div>
            <p className="sub-title-page mb-0">Okunma Oranı</p>
          </div>
        </CardContainer>
      </div>
      <div className="col-md-6">
        <CardContainer>
          <div className="p-3">
            <h6 className="mb-3">Bildirim Türüne Göre</h6>
            {(data.byEventType || []).length === 0 ? (
              <p className="text-muted small mb-0">Veri yok</p>
            ) : (
              data.byEventType.map((e) => (
                <div
                  key={e._id}
                  className="d-flex justify-content-between py-2 border-bottom"
                >
                  <span className="small">
                    {EVENT_TYPE_LABELS[e._id] || e._id}
                  </span>
                  <strong>{e.count}</strong>
                </div>
              ))
            )}
          </div>
        </CardContainer>
      </div>
      <div className="col-md-6">
        <CardContainer>
          <div className="p-3">
            <h6 className="mb-3">Önceliğe Göre</h6>
            {(data.bySeverity || []).length === 0 ? (
              <p className="text-muted small mb-0">Veri yok</p>
            ) : (
              data.bySeverity.map((e) => (
                <div
                  key={e._id}
                  className="d-flex justify-content-between align-items-center py-2 border-bottom"
                >
                  <Lozenge
                    appearance={SEVERITY_LOZENGE[e._id] || "default-subtle"}
                    isBold
                  >
                    {SEVERITY_LABELS[e._id] || e._id}
                  </Lozenge>
                  <strong>{e.count}</strong>
                </div>
              ))
            )}
          </div>
        </CardContainer>
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const NotificationSettingsPage = () => {
  useTitle("Rovion MES - Bildirim Yönetimi");
  const { user, loading, getToken } = useAuth();
  const [toasts, setToasts] = useState([]);
  const [tabAction, setTabAction] = useState(null);

  const registerTabAction = useCallback((action) => {
    setTabAction(action);
  }, []);

  const addToast = useCallback((message, type = "success") => {
    setToasts((p) => [...p, { id: Date.now(), message, type }]);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((p) => p.filter((t) => t.id !== id));
  }, []);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "40vh" }}
      >
        <Loading />
      </div>
    );
  }

  if (!isNotificationAdmin(user)) {
    return <Navigate to="/notifications" replace />;
  }

  return (
    <AppPage
      title="Bildirim Yönetimi"
      actions={
        tabAction ? (
          <Button
            appearance="primary-animated"
            icon={<FiPlus />}
            onClick={tabAction.onClick}
          >
            {tabAction.label}
          </Button>
        ) : null
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type === "danger" ? "danger" : "success"}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      <CardContainer>
        <Tabs>
          <TabList>
            <Tab>
              <span className="d-flex align-items-center gap-2">
                <FiFileText size={14} /> Şablonlar
              </span>
            </Tab>
            <Tab>
              <span className="d-flex align-items-center gap-2">
                <FiGitMerge size={14} /> Kurallar
              </span>
            </Tab>
          </TabList>
          <TabPanel>
            <Panel className="pt-3">
              <TemplatesTab
                getToken={getToken}
                addToast={addToast}
                onRegisterActions={registerTabAction}
              />
            </Panel>
            <Panel className="pt-3">
              <RulesTab
                user={user}
                getToken={getToken}
                addToast={addToast}
                onRegisterActions={registerTabAction}
              />
            </Panel>
          </TabPanel>
        </Tabs>
      </CardContainer>
    </AppPage>
  );
};

export default withAuth(NotificationSettingsPage);
